import { CircleHelpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import { cn } from '@/lib/utils';
import { selectBrainId } from '@/redux/session/selectors';

import { KnowledgeBaseSelector } from './KnowledgeBaseSelector';
import { PersonalizationWebhookInfo } from './PersonalizationWebhookInfo';
import { ToolsSelector } from './ToolsSelector';

type GuidelinesHeaderProps = {
  onSave?: () => void;
  hasUnsavedChanges?: boolean;
  remainingCharacters?: number;
  className?: string;
  helpTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
};

export const GuidelinesHeader = ({
  onSave,
  hasUnsavedChanges = false,
  remainingCharacters = 0,
  className,
  helpTooltipSide = 'right',
}: GuidelinesHeaderProps) => {
  const brainId = useSelector(selectBrainId);
  const { t } = useTranslation();
  const { brain } = useBrains(brainId);

  if (!brain?.guidelines) {
    return null;
  }

  const isCharacterLimitExceeded = remainingCharacters < 0;

  const handleSave = () => {
    if (isCharacterLimitExceeded) {
      return;
    }
    onSave?.();
  };

  return (
    <div className={cn('flex justify-between', className)}>
      <div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 text-xs leading-4 font-medium text-muted-foreground">
            {t('collections.collection')}
            <InfoTooltip
              content={t('ai_agents.guidelines.knowledge_tooltip')}
              side={helpTooltipSide}
            >
              <CircleHelpIcon size={12} className="text-muted-foreground" />
            </InfoTooltip>
          </div>

          <KnowledgeBaseSelector />
        </div>
      </div>
      <PersonalizationWebhookInfo helpTooltipSide={helpTooltipSide} />
      <ToolsSelector helpTooltipSide={helpTooltipSide} />

      {onSave && (
        <InfoTooltip
          open={isCharacterLimitExceeded || hasUnsavedChanges ? undefined : false}
          content={
            isCharacterLimitExceeded
              ? t('ai_agents.saving_status.unable_to_save_changes')
              : t('ai_agents.saving_status.there_are_unsaved_changes')
          }
        >
          <div className="relative">
            <Button type="button" size="sm" onClick={handleSave}>
              {t('common.save')}
            </Button>

            {hasUnsavedChanges && !isCharacterLimitExceeded && (
              <Badge
                type="bordered"
                flavour="destructive"
                size="md"
                className="absolute -top-1 -right-1 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]"
                aria-label={t('ai_agents.saving_status.there_are_unsaved_changes')}
              />
            )}
          </div>
        </InfoTooltip>
      )}
    </div>
  );
};
